import * as XLSX from "xlsx";
import { useState } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { convertDayJsInDate } from "~helpers/chat";
import { useSocketContext } from "~contexts/Socket";
import { getStyles } from "./styles";
import type { IMessage } from "~contexts/Socket/interfaces";
import type { Dayjs } from "dayjs";
import {
  CalendarIcon,
  DownloadIcon,
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from "~components/Icons";

interface IProps {
  setModalOpened: (value: boolean) => void;
}

const SLOTS = {
  openPickerIcon: CalendarIcon,
  leftArrowIcon: CaretLeftIcon,
  rightArrowIcon: CaretRightIcon,
  switchViewIcon: CaretDownIcon,
};

const ChatModal = ({ setModalOpened }: IProps) => {
  const theme: any = useTheme();
  const { getChatHistory } = useSocketContext();
  const [value, setValue] = useState<Dayjs | null>(null);
  const [chatHistory, setChatHistory] = useState<IMessage[]>([]);
  const { styles } = getStyles(theme);

  const formatLeadingZero = (dateNumber: number) => `0${String(dateNumber)}`.slice(-2);

  const setHistory = async (todayDateString: string) => {
    const [year, month, day] = todayDateString.split("-");
    const tomorrow = new Date(Number(year), Number(month) - 1, Number(day) + 1);
    const _year = tomorrow.getFullYear();
    const _month = formatLeadingZero(tomorrow.getMonth() + 1);
    const _day = formatLeadingZero(tomorrow.getDate());
    const tomorrowDateString = `${_year}-${_month}-${_day}`;
    const todayMessages = await getChatHistory(todayDateString);
    const tomorrowMessages = await getChatHistory(tomorrowDateString);
    const filterByDate = (messagesList: any) => {
      return messagesList.filter(
        (msg: IMessage) => Number(day) === new Date(Number(msg.createdAt)).getDate()
      );
    };

    setChatHistory([...filterByDate(todayMessages), ...filterByDate(tomorrowMessages)]);
  };

  const onDownloadXLSXChat = () => {
    if (!chatHistory || !chatHistory.length) return;

    const headers = ["data", "hora", "email", "respondendo para", "mensagem"];
    const aoa = [headers].concat(
      chatHistory.map(({ createdAt, text, email, replyingTo }: IMessage) => {
        const creationDatetime = new Date(createdAt || 0);
        let replyTarget = "";
        if (replyingTo && /^(.+T.+Z)?(.+@.+)$/.exec(replyingTo)) {
          replyTarget = /^(.+T.+Z)?(.+@.+)$/.exec(replyingTo)![2];
        }

        return [
          `${formatLeadingZero(creationDatetime.getDate())}/${formatLeadingZero(
            creationDatetime.getMonth() + 1
          )}/${creationDatetime.getFullYear()}`,
          creationDatetime.toLocaleTimeString(),
          email,
          replyTarget,
          text,
        ];
      })
    );

    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Chat");
    XLSX.writeFile(workbook, `somalive-${convertDayJsInDate(value)}-chat.xlsx`);
  };

  return (
    <Box>
      <Typography style={{ color: theme.palette.grey[0], fontSize: "24px" }}>
        download do histórico de chat
      </Typography>
      <Typography style={{ fontSize: "14px", color: theme.palette.grey[400] }}>
        selecione uma data para fazer o download do histórico
      </Typography>
      <Box sx={{ margin: "24px 0" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={value}
            slots={SLOTS}
            format="DD/MM/YYYY"
            label="escolha a data"
            onChange={(value) => {
              setHistory(convertDayJsInDate(value));
              setValue(value);
            }}
          />
        </LocalizationProvider>
      </Box>

      <Box style={styles.buttonsStyle}>
        <Button
          sx={styles.buttonDownloadStyle}
          onClick={() => onDownloadXLSXChat()}
          disabled={!chatHistory || !chatHistory.length}
          endIcon={<DownloadIcon opacity={!!chatHistory.length ? 1 : 0.4} />}
        >
          download
        </Button>
        <Button onClick={() => setModalOpened(false)} style={styles.buttonCancelStyle}>
          cancelar
        </Button>
      </Box>
    </Box>
  );
};

export default ChatModal;
